import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedSearch } from '@/hooks/useDebouncedValue';
import { api } from '@/lib/api';

interface AcceptanceRow {
  id: string;
  userName: string;
  userEmail: string;
  policyTitle: string;
  policySlug: string;
  policyVersion: string;
  acceptedAt: string;
  source: string;
}

function acceptanceSourceLabel(source: string): string {
  switch (source) {
    case 'contract_signing':
      return 'Firma de contrato';
    case 'register':
      return 'Registro';
    default:
      return 'Aceptación directa';
  }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    parts
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || '?'
  );
}

export function LegalPolicyAcceptancesPage() {
  const { value: search, setValue: setSearch, debounced, page, setPage } = useDebouncedSearch();
  const appliedSearch = debounced.trim();

  const { data, isLoading, error } = useQuery({
    queryKey: ['legal-policies', 'acceptances', page, appliedSearch],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), perPage: '20' });
      if (appliedSearch) params.set('search', appliedSearch);
      return api<{ data: AcceptanceRow[]; meta: { total: number; page: number; perPage: number; lastPage: number } }>(
        `/legal/policies/acceptances?${params}`,
      );
    },
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const perPage = meta?.perPage ?? 20;
  const total = meta?.total ?? 0;
  const lastPage = meta?.lastPage ?? 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);

  return (
    <div className="lms-legal-policies-admin lms-site-admin-hub-page font-sans">
      <header className="lms-legal-policies-admin__page-header mb-6">
        <div className="flex items-start gap-4">
          <HubBackLink to="/politicas-legales" label="Volver a Políticas legales" />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Aceptaciones registradas</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Auditoría de consentimientos legales aceptados por los usuarios.
            </p>
          </div>
        </div>
        <div className="lms-legal-policies-admin__actions">
          <Link to="/politicas-legales" className="lms-legal-policies-admin__btn-primary">
            <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
              add
            </span>
            Nueva plantilla
          </Link>
        </div>
      </header>

      <form
        className="lms-credentials-glass-panel mb-6 overflow-hidden rounded-xl p-6 shadow-sm"
        onSubmit={(event) => event.preventDefault()}
      >
        <SearchInput
          id="acceptance-student-search"
          name="search"
          value={search}
          onChange={setSearch}
          placeholder="Buscar alumno por nombre o correo…"
          aria-label="Buscar alumno"
        />
      </form>

      {error ? <p className="text-sm text-red-600">No se pudieron cargar las aceptaciones.</p> : null}

      {!error ? (
        <div className="lms-gradebook lms-gradebook--forum">
          <div className="lms-course-forum-card">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader label="Cargando aceptaciones…" />
              </div>
            ) : rows.length === 0 ? (
              <div className="lms-gradebook__table-wrap">
                <div className="lms-gradebook__empty">
                  <div className="lms-gradebook__empty-icon" aria-hidden="true">
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                      policy
                    </span>
                  </div>
                  <h3>{appliedSearch ? 'Sin resultados' : 'Sin aceptaciones'}</h3>
                  <p>
                    {appliedSearch
                      ? 'No hay aceptaciones de ese alumno.'
                      : 'No hay aceptaciones registradas.'}
                  </p>
                  {appliedSearch ? (
                    <button type="button" className="lms-gradebook__clear" onClick={() => setSearch('')}>
                      Restablecer filtros
                    </button>
                  ) : null}
                </div>
              </div>
            ) : (
              <>
                <div className="lms-gradebook__table-wrap">
                  <table className="lms-gradebook__table">
                    <thead>
                      <tr>
                        <th className="lms-gradebook__th" scope="col">
                          Usuario
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Política
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Versión
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Origen
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Fecha
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id}>
                          <th className="lms-gradebook__td" scope="row">
                            <div className="lms-gradebook__student">
                              <div className="lms-gradebook__avatar" aria-hidden="true">
                                {initials(row.userName)}
                              </div>
                              <div className="min-w-0">
                                <span className="lms-gradebook__name">{row.userName}</span>
                                <div className="lms-gradebook__muted">{row.userEmail}</div>
                              </div>
                            </div>
                          </th>
                          <td className="lms-gradebook__td">{row.policyTitle}</td>
                          <td className="lms-gradebook__td">{row.policyVersion}</td>
                          <td className="lms-gradebook__td">{acceptanceSourceLabel(row.source)}</td>
                          <td className="lms-gradebook__td lms-gradebook__td--nowrap">
                            {new Date(row.acceptedAt).toLocaleString('es-ES')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {total > 0 ? (
                  <div className="lms-course-forum-card__footer">
                    <span className="lms-course-forum-card__meta">
                      Mostrando <strong>{rangeStart}</strong> a <strong>{rangeEnd}</strong> de <strong>{total}</strong>{' '}
                      resultados
                    </span>
                    <div className="lms-course-forum-card__pager">
                      <button
                        type="button"
                        className="lms-course-forum-card__pager-btn"
                        aria-label="Página anterior"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">
                          chevron_left
                        </span>
                      </button>
                      <span className="lms-course-forum-card__pager-current">{page}</span>
                      <button
                        type="button"
                        className="lms-course-forum-card__pager-btn"
                        aria-label="Página siguiente"
                        disabled={page >= lastPage}
                        onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">
                          chevron_right
                        </span>
                      </button>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
